<template>
  <tab-layout :full-width="true">
    <div slot="tabContent">

      <div class="bg-white rounded mb-4 p-3">
        <page-heading>Edit Draft</page-heading>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>

        <template v-else-if="!loadFailed">
        <form :key="formKey" class="needs-validation" novalidate @submit.prevent="handleSubmit">
          <draft-details-section
            :value="form"
            :edit="true"
            :states="draftStates"
            :loading-states="loadingStates"
            :errors="errors"
            @input="updateForm"
          />
          <draft-rules-section :value="form" :errors="errors" @input="updateForm" />
          <medical-services-section :value="form" :errors="errors" @input="updateForm" />
          <payment-details-section :value="form" :errors="errors" :is-stripe-verified="isStripeVerified" @input="updateForm" />

          <div class="mt-4">
            <app-button type="submit" :block="false" :loading="submitting">
              Save Details
            </app-button>
          </div>
        </form>

        <auto-generate-program-section
          :value="form"
          :generating="generating"
          :approving="approving"
          :busy="autogenBusy"
          :alert-message="autogenAlert.message.value"
          :alert-type="autogenAlert.type.value"
          @input="updateForm"
          @color-change="handleColorChange"
          @generate="handleGenerate"
          @approve="handleApprove"
        />
        </template>

      </div>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, reactive, ref, onMounted, getCurrentInstance } from '@vue/composition-api'
import EventBus from '@/store/EventBus'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import useDraftStates from '@/composables/drafts/useDraftStates'
import useDraftForm, { defaultDraftForm } from '@/composables/drafts/useDraftForm'
import useAutoGenerateProgram from '@/composables/drafts/useAutoGenerateProgram'
import AppButton from '@/components/AppButton'
import DraftDetailsSection from '@/components/Drafts/DraftDetailsSection'
import DraftRulesSection from '@/components/Drafts/DraftRulesSection'
import MedicalServicesSection from '@/components/Drafts/MedicalServicesSection'
import PaymentDetailsSection from '@/components/Drafts/PaymentDetailsSection'
import AutoGenerateProgramSection from '@/components/Drafts/AutoGenerateProgramSection'

export default defineComponent({
  name: 'SecretaryDraftEdit',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    DraftDetailsSection,
    DraftRulesSection,
    MedicalServicesSection,
    PaymentDetailsSection,
    AutoGenerateProgramSection
  },
  props: {
    draftId: { type: [String, Number], required: true }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const form = reactive(defaultDraftForm())
    const { states: draftStates, loading: loadingStates } = useDraftStates()
    const { errors, submitting, updateDraft, loading, loadFailed, isStripeVerified, loadDraft, reloadDraft } = useDraftForm(form)
    const formKey = ref(0)
    const {
      generating, approving, busy: autogenBusy, alert: autogenAlert, saveColor, generate, approve
    } = useAutoGenerateProgram()

    onMounted(async () => {
      await loadDraft(props.draftId)
      formKey.value += 1
    })

    function updateForm(updated) {
      Object.assign(form, updated)
    }

    async function handleSubmit() {
      const message = await updateDraft(props.draftId)
      if (message) {
        proxy.$router.push({ name: 'Committees Dashboard' }, () => {
          proxy.$nextTick(() => {
            EventBus.$emit('show-success', typeof message === 'string' ? message : 'Success')
          })
        })
      }
    }

    function handleColorChange(color) {
      saveColor(props.draftId, color)
    }

    async function handleGenerate() {
      const url = await generate(props.draftId)
      if (url != null) form.temporary_program = url
    }

    async function handleApprove() {
      const ok = await approve(props.draftId)
      if (ok) reloadDraft(props.draftId)
    }

    return {
      form,
      formKey,
      updateForm,
      draftStates,
      loadingStates,
      errors,
      submitting,
      loading,
      loadFailed,
      isStripeVerified,
      handleSubmit,
      generating,
      approving,
      autogenAlert,
      autogenBusy,
      handleColorChange,
      handleGenerate,
      handleApprove
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Drafts/Edit/SecretaryDraftEdit.vue